/** Pull JWT/access token from common API response shapes (login / refresh). */
export function extractJwtFromLoginBody(data: unknown): string | null {
  if (data == null) return null
  if (typeof data === 'string' && data.trim().length > 0) return data.trim()

  if (typeof data !== 'object') return null
  const d = data as Record<string, unknown>

  const direct = [
    d.JWT,
    d.jwt,
    d.token,
    d.Token,
    d.accessToken,
    d.access_token,
    d.AccessToken,
    d.idToken,
    d.IdToken,
    d.sessionToken,
    d.SessionToken,
    d.authToken,
    d.AuthToken,
  ]
  for (const c of direct) {
    if (typeof c === 'string' && c.length > 0) return c
  }

  for (const key of [
    'data',
    'result',
    'Data',
    'Result',
    'user',
    'User',
  ] as const) {
    const nested = d[key]
    if (nested != null) {
      const found = extractJwtFromLoginBody(nested)
      if (found) return found
    }
  }

  return null
}

/** OTP verify response: setup / scope token for onboarding (profile + set-password). */
export function extractSetupTokenFromBody(data: unknown): string | null {
  if (data == null || typeof data !== 'object') return null
  const d = data as Record<string, unknown>
  const direct = [
    d.setupToken,
    d.SetupToken,
    d.setup_token,
    d.scopeToken,
    d.ScopeToken,
  ]
  for (const c of direct) {
    if (typeof c === 'string' && c.length > 0) return c
  }
  for (const key of ['data', 'result', 'Data', 'Result'] as const) {
    const nested = d[key]
    if (nested != null) {
      const found = extractSetupTokenFromBody(nested)
      if (found) return found
    }
  }
  return null
}

type AuthBodyUser = {
  id: string
  email: string
  handle?: string | null
  full_name?: string | null
}

/** User id + email (and handle / full_name when present) from login / set-password / GET me-style responses. */
export function extractUserFromAuthBody(
  data: unknown,
  fallbackEmail: string,
): AuthBodyUser | null {
  const pickStr = (v: unknown): string | undefined =>
    typeof v === 'string' && v.length > 0 ? v : undefined

  const walk = (o: unknown, depth: number): AuthBodyUser | null => {
    if (depth > 6 || o == null || typeof o !== 'object') return null
    const r = o as Record<string, unknown>
    const idRaw = r.id ?? r.userId ?? r.UserId ?? r.user_id ?? r.User_Id
    const emailRaw = r.email ?? r.Email ?? r.EmailID ?? r.emailId
    if (typeof idRaw === 'string' && idRaw.length > 0) {
      const email =
        typeof emailRaw === 'string' && emailRaw.length > 0
          ? emailRaw.trim()
          : fallbackEmail.trim()
      const handle = pickStr(r.handle ?? r.Handle ?? r.user_handle) ?? null
      const full_name =
        pickStr(r.full_name ?? r.fullName ?? r.FullName ?? r.name) ?? null
      return { id: idRaw, email, handle, full_name }
    }
    for (const key of [
      'user',
      'User',
      'data',
      'Data',
      'result',
      'Result',
      'payload',
      'Payload',
    ] as const) {
      const nested = r[key]
      if (nested != null) {
        const found = walk(nested, depth + 1)
        if (found) return found
      }
    }
    return null
  }
  return walk(data, 0)
}
